import { router } from "@inertiajs/react"
import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { BrandSplashCoverBand } from "@/components/brand/brand-splash-cover-band"
import {
  CoverCropper,
  type CoverCropperLabels,
} from "@/components/cover-cropper"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  SPLASH_COVER_ASPECT_CLASS,
  SPLASH_COVER_ASPECT_RATIO,
} from "@/lib/splash-cover"
import { cn } from "@/lib/utils"
import { brandSettingsProfileSplashCoverImagePath } from "@/routes"

/**
 * OKS-857 — Brand splash (portrait 9:16) image upload + remove widget for
 * the Brand Settings → Profile page.
 *
 * Sits directly BELOW the `<BrandCoverImageUploader />` per UX Spec §2 —
 * its own labelled field, distinct ratio and purpose (the landscape cover
 * vs the phone splash background). Mirrors the cover uploader's affordance:
 * a hidden `<input type="file" name="brand[splash_cover_image]">` inside the
 * parent Inertia `<Form>` (multipart auto-detected by Inertia when a file
 * input is present).
 *
 * Every pick/replace of an accepted image routes through the shared
 * `CoverCropper` (a 9:16 `react-easy-crop` step) BEFORE anything is staged;
 * only the cropped 9:16 Blob is injected back (via `DataTransfer`) and
 * submitted, so no uncropped image reaches the server and the framed area is
 * exactly what the splash shows (client-crop-authoritative, mirroring the
 * OKS-855 brand-cover / OKS-768 masterclass pattern). A non-image pick
 * bypasses the cropper and defers to server content-type validation.
 *
 * Affordances (UX Spec §2):
 *   - Replace → opens the file picker; an accepted image opens the 9:16
 *     cropper, a non-image stays on the input for server validation.
 *   - Remove (gated on `currentUrl`) → destructive `<Dialog>` with Cancel
 *     as primary-focus; confirm fires
 *     `router.delete(brandSettingsProfileSplashCoverImagePath())`.
 *   - Read-only mode (Brand Member / Agency Member): hides Replace + Remove,
 *     leaves the preview band visible.
 *
 * Edit-load skeleton (UX Spec §3): gates ONLY the server-provided
 * `currentUrl` branch and settles when the server image resolves EITHER way
 * — `onImageLoad` (paint) OR `onImageError` (a 404 from an expired/purged
 * signed URL, which never fires onLoad). Settling on error is what lets the
 * band's dark fallback show instead of a stuck skeleton (the OKS-855
 * 404→infinite-skeleton bug, avoided here by construction).
 *
 * Crew form rules: plain shadcn `Input`/`Label`/`Button`/`Dialog`; NO
 * shadcn Form / react-hook-form / zod. The crop widget is NOT a form.
 * Errors render from the parent `<Form>` render-prop `errors.splash_cover_image`.
 *
 * Brand isolation: presentation-only; `currentUrl` + `brandName` come from
 * server-rendered Inertia props derived from `Current.brand` only.
 */

/**
 * Accepted splash-image MIME types (mirrors the server's
 * `Brand::SPLASH_COVER_IMAGE_CONTENT_TYPES`). Drives BOTH the file input's
 * `accept` filter AND the crop-vs-bypass decision: a non-image type can't be
 * framed, so it skips the cropper and defers to server validation.
 */
export const SPLASH_COVER_ACCEPT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
]

const SPLASH_COVER_CROP_LABELS: CoverCropperLabels = {
  title: "settings.brand.profile.splash_cover_crop_title",
  description: "settings.brand.profile.splash_cover_crop_description",
  zoom: "settings.brand.profile.splash_cover_crop_zoom",
  cancel: "settings.brand.profile.splash_cover_crop_cancel",
  apply: "settings.brand.profile.splash_cover_crop_apply",
  decodeError: "settings.brand.profile.splash_cover_crop_decode_error",
}

export interface BrandSplashCoverImageUploaderProps {
  /** Current splash-image URL from Inertia props
   *  (`brand.splash_cover_image_url`) — null when none attached. */
  currentUrl: string | null
  /** Brand display name — passed to `<BrandSplashCoverBand />` so the dark
   *  fallback monogram has the right initial. */
  brandName: string
  /** Wire-format strong-params input name. Default `brand[splash_cover_image]`
   *  matches `BrandSettingsProfilesController#profile_params`. */
  inputName?: string
  /** Disable affordances for Brand Member / Agency Member read-only tiers. */
  readOnly?: boolean
  /** Test-id wrapper. */
  testId?: string
}

export function BrandSplashCoverImageUploader({
  currentUrl,
  brandName,
  inputName = "brand[splash_cover_image]",
  readOnly = false,
  testId = "brand-splash-cover-image-uploader",
}: BrandSplashCoverImageUploaderProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removeProcessing, setRemoveProcessing] = useState(false)
  // Crop step. A picked/replaced accepted image opens the cropper; only the
  // cropped (9:16) Blob is ever staged into the hidden multipart input.
  const [cropOpen, setCropOpen] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropMime, setCropMime] = useState<string | undefined>(undefined)
  // Edit-load skeleton — gates ONLY the server-provided `currentUrl` branch.
  // Resets whenever `currentUrl` changes (React's "reset state on prop
  // change" pattern) so a stale "loaded" flag can't hide a fresh skeleton.
  const [currentUrlLoaded, setCurrentUrlLoaded] = useState(false)
  const [trackedCurrentUrl, setTrackedCurrentUrl] = useState(currentUrl)
  if (currentUrl !== trackedCurrentUrl) {
    setTrackedCurrentUrl(currentUrl)
    setCurrentUrlLoaded(false)
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setPreviewUrl(null)
      return
    }

    // A NON-image type can't be framed by the cropper. Bypass the crop step
    // and leave the raw file on the input so the server's content-type
    // validation produces the canonical inline error
    // (`errors.splash_cover_image`) on Save.
    if (!SPLASH_COVER_ACCEPT_TYPES.includes(file.type)) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(file)
      })
      return
    }

    // Image: route every pick/replace through the crop step. The OS picker
    // populates the hidden input with the UNCROPPED original; clear it
    // immediately so a form submit before *Apply* can't send the raw file.
    // Only `onCropApply` re-populates the input (with the 9:16 Blob).
    if (inputRef.current) inputRef.current.value = ""
    setCropMime(file.type || undefined)
    setCropSrc(URL.createObjectURL(file))
    setCropOpen(true)
  }

  // Apply the cropped 9:16 Blob: wrap it in a File, inject it into the hidden
  // multipart input via `DataTransfer` (the only way to programmatically set
  // `input.files`), and update the preview.
  const onCropApply = (blob: Blob) => {
    const ext =
      blob.type === "image/png"
        ? "png"
        : blob.type === "image/webp"
          ? "webp"
          : "jpg"
    const file = new File([blob], `brand-splash-cover.${ext}`, {
      type: blob.type || "image/jpeg",
    })
    if (inputRef.current) {
      const transfer = new DataTransfer()
      transfer.items.add(file)
      inputRef.current.files = transfer.files
    }
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(blob)
    })
    closeCropper()
  }

  const onCropCancel = () => {
    // No file staged — the input stays cleared, the prior preview/image is
    // untouched.
    closeCropper()
  }

  const closeCropper = () => {
    setCropOpen(false)
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setCropMime(undefined)
  }

  const onClear = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    if (inputRef.current) inputRef.current.value = ""
  }

  const onReplaceClick = () => {
    inputRef.current?.click()
  }

  const handleRemove = () => {
    setRemoveProcessing(true)
    router.delete(brandSettingsProfileSplashCoverImagePath(), {
      preserveScroll: true,
      onFinish: () => {
        setRemoveProcessing(false)
        setRemoveOpen(false)
        onClear()
      },
    })
  }

  // Preview shows: the picked-but-not-yet-saved file > the persisted image
  // URL > nothing (band falls back to dark monogram).
  const displayUrl = previewUrl ?? currentUrl
  // True only while `displayUrl` IS the server-provided `currentUrl` (no
  // local pick in front of it) — the only branch the edit-load skeleton
  // applies to.
  const showingServerCover = !previewUrl && Boolean(currentUrl)

  return (
    <div data-testid={testId} className="space-y-3">
      <Label htmlFor={inputId}>
        {t("settings.brand.profile.splash_cover_label")}
      </Label>

      <div className="w-full max-w-xs">
        {/* Edit-load skeleton: shown only while the server-provided
            `currentUrl` is loading for the first time; the band stays
            mounted (so the image's `onLoad`/`onError` still fires) but
            visually hidden underneath until it resolves. */}
        {showingServerCover && !currentUrlLoaded ? (
          <Skeleton
            data-testid={`${testId}-loading-skeleton`}
            className={cn(SPLASH_COVER_ASPECT_CLASS, "w-full rounded-lg")}
          />
        ) : null}
        <BrandSplashCoverBand
          coverUrl={displayUrl}
          brandName={brandName}
          onImageLoad={() => {
            if (showingServerCover) setCurrentUrlLoaded(true)
          }}
          onImageError={() => {
            // A 404 on the server image settles the skeleton the OTHER way
            // so the dark fallback band shows (never a stuck loader).
            if (showingServerCover) setCurrentUrlLoaded(true)
          }}
          className={cn(
            "rounded-lg",
            showingServerCover && !currentUrlLoaded && "hidden",
          )}
          testId={`${testId}-preview`}
        />
      </div>

      <p className="text-muted-foreground max-w-2xl text-xs">
        {t("settings.brand.profile.splash_cover_helper")}
      </p>

      {/* Hidden file input is ALWAYS in the DOM (read-only or not) so the
          parent Inertia `<Form>` auto-detects multipart. */}
      {readOnly ? null : (
        <Input
          ref={inputRef}
          id={inputId}
          type="file"
          name={inputName}
          accept={SPLASH_COVER_ACCEPT_TYPES.join(",")}
          onChange={onPick}
          className="sr-only"
          data-testid={`${testId}-input`}
          aria-label={t("settings.brand.profile.splash_cover_label")}
        />
      )}

      {readOnly ? null : (
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onReplaceClick}
            data-testid={`${testId}-replace`}
            className="min-h-11"
          >
            {t("settings.brand.profile.splash_cover_replace")}
          </Button>

          {previewUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="text-muted-foreground sm:self-center"
            >
              {t("settings.brand.profile.splash_cover_clear")}
            </Button>
          ) : null}

          {currentUrl ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRemoveOpen(true)}
              data-testid={`${testId}-remove`}
              className="min-h-11 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t("settings.brand.profile.splash_cover_remove")}
            </Button>
          ) : null}
        </div>
      )}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid={`${testId}-remove-dialog`}>
          <DialogTitle>
            {t("settings.brand.profile.splash_cover_remove_confirm_title")}
          </DialogTitle>
          <DialogDescription>
            {t("settings.brand.profile.splash_cover_remove_confirm_body")}
          </DialogDescription>
          <DialogFooter>
            {/* Cancel is primary-focus — destructive action is NEVER the
                default focus target of a Dialog. */}
            <DialogClose
              render={
                <Button
                  variant="secondary"
                  autoFocus
                  disabled={removeProcessing}
                  data-testid={`${testId}-remove-cancel`}
                >
                  {t(
                    "settings.brand.profile.splash_cover_remove_confirm_cancel",
                  )}
                </Button>
              }
            />
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={removeProcessing}
              data-testid={`${testId}-remove-confirm`}
            >
              {t("settings.brand.profile.splash_cover_remove_confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crop-to-9:16 step for every accepted image pick/replace. */}
      <CoverCropper
        open={cropOpen}
        imageSrc={cropSrc}
        mimeType={cropMime}
        aspectRatio={SPLASH_COVER_ASPECT_RATIO}
        aspectClass={SPLASH_COVER_ASPECT_CLASS}
        labels={SPLASH_COVER_CROP_LABELS}
        onApply={onCropApply}
        onCancel={onCropCancel}
        testId="brand-splash-cover-crop"
      />
    </div>
  )
}

export default BrandSplashCoverImageUploader
